<script lang="ts">
    import { createEventDispatcher } from "svelte";

    //Types
    type Type = "text" | "number" | "tel" | "email" | "password" | "checkbox";
    type InputMode = "none" | "text" | "decimal" | "numeric" | "tel";
    type AriaAutocomplete = "none" | "inline" | "list" | "both";
    type Alignment = "left" | "center" | "right";

    //Optional props
    export let type: Type = "text";
    export let inputmode: InputMode = "text";
    export let placeholder: string | null = null;
    export let element: HTMLInputElement | null = null;
    export let value: string | null = null;
    export let ariaLabel: string | null = null;
    export let role: string | null = null;
    export let ariaControls: string | null = null;
    export let ariaActivedescendant: string | null = null;
    export let ariaAutocomplete: AriaAutocomplete = "none";
    export let ariaExpanded = false;
    export let tabindex = 0;
    export let id: string | null = null;
    export let checked: boolean | null = null;
    export let disabled = false;
    export let name: string | null = null;
    export let autocomplete: string | null = null;
    export let readonly: boolean | null = null;
    export let min: number | null = null;
    export let max: number | null = null;
    export let step: number | null = null;
    export let textAlign: Alignment = "left";
    export let onChange: ((value: string) => void) | null = null;

    //Dispatcher
    const dispatch = createEventDispatcher<{
        change: { value: string; event: Event };
        blur: { value: string; event: Event };
    }>();

    //Handlers
    const handleInput = (event: Event) => {
        if (!(event.target instanceof HTMLInputElement)) return;

        onChange && onChange(event.target.value);
        dispatch("change", { value: event.target.value, event });
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        const numbersSpec = /^[0-9.]+$/g;

        if (type === "number" && !numbersSpec.test(event.key))
            event.preventDefault();
    };
</script>

<div class="gw-rd-text-field">
    {#if $$slots.prefix}
        <div class="gw-rd-text-field__prefix" data-gw-selector="input_prefix">
            <slot name="prefix" />
        </div>
    {/if}
    <input
        class="gw-rd-text-field__input gw-rd-text-field__input--{textAlign}"
        class:gw-rd-text-field-type-number={type === "number"}
        {type}
        {inputmode}
        {value}
        {placeholder}
        {tabindex}
        {role}
        {id}
        {disabled}
        {name}
        {autocomplete}
        {readonly}
        {min}
        {max}
        {step}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-activedescendant={ariaActivedescendant}
        aria-checked={checked}
        aria-autocomplete={ariaAutocomplete}
        aria-valuemin={min}
        aria-valuemax={max}
        on:input={handleInput}
        on:focus
        on:blur
        on:click
        on:keypress={handleKeyPress}
        bind:this={element}
    />
    {#if $$slots.suffix}
        <div class="gw-rd-text-field__suffix">
            <slot name="suffix" />
        </div>
    {/if}
</div>

<style lang="postcss">
    .gw-rd-text-field {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        padding-inline: var(--gw-rd-space-6);
        font-size: var(--gw-font-size-sm);
        border: 1px solid var(--gw-rd-field-border-color);
        border-radius: var(--gw-rd-border-radius-xs);
        color: var(--gw-rd-primary-color);

        &:focus-within {
            outline: 2px solid var(--gw-rd-primary-color);
        }

        &__input {
            width: 100%;
            flex: 1;
            padding: var(--gw-rd-space-6) var(--gw-rd-space-3);
            color: var(--gw-rd-primary-color);
            background: none;
            border: none;
            font-size: var(--gw-font-size-sm);
            text-overflow: ellipsis;
            font-family: inherit;

            &--left {
                text-align: left;
            }

            &--center {
                text-align: center;
            }

            &--right {
                text-align: right;
            }

            &::placeholder {
                opacity: 0.8;
                color: var(--gw-rd-primary-color);
            }

            &:disabled {
                opacity: 0.6;
            }

            &:focus-visible {
                outline: none;
            }
        }

        &__prefix,
        &__suffix {
            user-select: none;
        }
    }

    .gw-rd-text-field-type-number::-webkit-outer-spin-button,
    .gw-rd-text-field-type-number::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .gw-rd-text-field-type-number[type="number"] {
        -moz-appearance: textfield;
    }
</style>
